import React, { useState, useRef, useEffect } from 'react';
import { Button as MuiButton, Box } from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledIconHoverButton = styled(MuiButton)(({ theme, variant = 'contained', color = 'primary' }) => ({
  textTransform: 'none',
  fontWeight: 500,
  borderRadius: theme.shape.borderRadius,
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out, padding 0.2s ease-in-out',
  minWidth: 'auto',
  padding: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  '& .MuiButton-startIcon': {
    margin: 0,
    transition: 'none',
  },
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
    minWidth: 'auto',
    padding: theme.spacing(1, 2),
  },
  '&:active': {
    transform: 'translateY(0)',
  },
}));

const IconHoverButton = ({
  children,
  icon,
  variant = 'contained',
  color = 'primary',
  size = 'small',
  disabled = false,
  onClick,
  ...props
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const hoverTimeoutRef = useRef(null);
  const isHoveredRef = useRef(false);

  const handleMouseEnter = () => {
    isHoveredRef.current = true;
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setIsHovered(true);
  };

  const handleMouseLeave = (e) => {
    isHoveredRef.current = false;

    // Check if we're moving to a related element (another button)
    if (
      e.relatedTarget &&
      typeof e.relatedTarget.closest === 'function' &&
      e.relatedTarget.closest('.MuiButton-root')
    ) {
      // Small delay to allow the next button's mouseEnter to fire first
      hoverTimeoutRef.current = setTimeout(() => {
        if (!isHoveredRef.current) {
          setIsHovered(false);
        }
      }, 50);
    } else {
      // Immediate hide if not moving to another button
      hoverTimeoutRef.current = setTimeout(() => {
        setIsHovered(false);
      }, 50);
    }
  };

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  return (
    <StyledIconHoverButton
      variant={variant}
      color={color}
      size={size}
      disabled={disabled}
      onClick={onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      startIcon={icon}
      {...props}
    >
      {isHovered && (
        <Box
          component="span"
          sx={{
            display: 'inline-block',
            lineHeight: 1,
            animation: 'fadeIn 0.2s ease-in-out',
            '@keyframes fadeIn': {
              from: { opacity: 0, transform: 'translateX(-10px)' },
              to: { opacity: 1, transform: 'translateX(0)' },
            },
          }}
        >
          {children}
        </Box>
      )}
    </StyledIconHoverButton>
  );
};

export default IconHoverButton;